import React from 'react';
import PropTypes from 'prop-types';
import Button from '@material-ui/core/Button';
import MobileStepper from '@material-ui/core/MobileStepper';
import arrayFromNumber from '@afosto/utils/array/arrayFromNumber';
import styled from 'styled-components';

const Pagination = styled(MobileStepper)`
  @media (max-width: ${props => props.theme.breakpoints.values.md - 1}px) {
    && {
      box-shadow: ${props => props.theme.shadows[2]};
      background-color: ${props => props.theme.palette.grey[100]};
    }
  }
`;

const PaginatedView = ({
  dataSource,
  render,
  renderSkeleton,
  onNext,
  nextLabel,
  onBack,
  backLabel,
  stepperProps,
  onFinish,
  finishLabel,
}) => {
  const { pagination: paginationModule, data = [], isLoadingData, setIsLoadingData } = dataSource;

  if (!paginationModule) {
    throw new Error(
      'The pagination module is required for using the paginatedView. Please add it to the modules of the dataSource.',
    );
  }

  const { itemCount = 0, activePage, nextPage, previousPage } = paginationModule;

  const activeStep = activePage;

  const handleNext = async () => {
    try {
      if (onNext) {
        await onNext();
      }
      setIsLoadingData(true);
      nextPage();
    } catch (error) {
      // Do nothing.
    }
  };

  const handleBack = async () => {
    try {
      if (onBack) {
        await onBack();
      }
      setIsLoadingData(true);
      previousPage();
    } catch (error) {
      // Do nothing.
    }
  };

  const handleFinish = async () => {
    try {
      if (onFinish) {
        await onFinish();
      }
    } catch (error) {
      // Do nothing.
    }
  };

  return (
    <>
      {dataSource.isLoadingData ? arrayFromNumber(1).map(renderSkeleton) : data.map(render)}
      <Pagination
        steps={itemCount}
        activeStep={activeStep}
        nextButton={
          activePage === itemCount - 1 && onFinish ? (
            <Button
              variant="contained"
              color="primary"
              onClick={handleFinish}
              disabled={isLoadingData}
            >
              {finishLabel}
            </Button>
          ) : (
            <Button
              variant="outlined"
              color="primary"
              onClick={handleNext}
              disabled={activeStep === itemCount - 1 || isLoadingData}
            >
              {nextLabel}
            </Button>
          )
        }
        backButton={
          <Button
            variant="outlined"
            color="primary"
            onClick={handleBack}
            disabled={activeStep === 0 || isLoadingData}
          >
            {backLabel}
          </Button>
        }
        {...stepperProps}
      />
    </>
  );
};

PaginatedView.propTypes = {
  dataSource: PropTypes.object.isRequired,
  render: PropTypes.func.isRequired,
  renderSkeleton: PropTypes.func.isRequired,
  onNext: PropTypes.func,
  nextLabel: PropTypes.string,
  onBack: PropTypes.func,
  backLabel: PropTypes.string,
  stepperProps: PropTypes.object,
  onFinish: PropTypes.func,
  finishLabel: PropTypes.string,
};

PaginatedView.defaultProps = {
  onNext: undefined,
  nextLabel: 'Next',
  onBack: undefined,
  backLabel: 'Back',
  stepperProps: { position: 'static', variant: 'text' },
  onFinish: undefined,
  finishLabel: 'Finish',
};

export default PaginatedView;
